import React, { useEffect, useMemo, useState } from 'react';
import PaymentQr from './PaymentQr';
import { useI18n } from '../i18n/I18nContext';
import { getMembershipCopy } from '../i18n/membershipCopy';
import { getLocalizedError } from '../i18n/errorCopy';
import {
  getProPaymentSettings,
  requestTellerPro,
  requestTellerProCancellation,
  subscribeTellerPro,
  TELLER_PRO_PRICE_EUR,
} from '../services/proMembership';

const TellerProPanel = ({ user, initialPlan = 'free' }) => {
  const { language } = useI18n();
  const copy = getMembershipCopy(language, 'teller');
  const [membership, setMembership] = useState({
    plan: initialPlan,
    upgradeOrder: null,
    cancellationOrder: null,
  });
  const [settings, setSettings] = useState({ proPriceEUR: TELLER_PRO_PRICE_EUR });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    getProPaymentSettings().then(setSettings);
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribe = subscribeTellerPro(
      user.uid,
      (next) => {
        setMembership(next);
        setLoading(false);
      },
      (nextError) => {
        setError(getLocalizedError(nextError, language, copy.loadError));
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [copy.loadError, language, user?.uid]);

  const state = useMemo(() => {
    const cancellationPending = membership.cancellationOrder?.status === 'pending';
    const active = membership.plan === 'pro';
    const upgradePending = ['pending', 'paid', 'approved'].includes(membership.upgradeOrder?.status) && !active;
    return { cancellationPending, active, upgradePending };
  }, [membership]);

  const activate = async () => {
    setWorking('activate');
    setError('');
    setMessage('');
    try {
      const result = await requestTellerPro(user);
      setSettings(result.settings);
      setMessage(copy.ready);
    } catch (nextError) {
      setError(getLocalizedError(nextError, language, copy.requestError));
    } finally {
      setWorking('');
    }
  };

  const cancel = async () => {
    setWorking('cancel');
    setError('');
    setMessage('');
    try {
      await requestTellerProCancellation(user);
      setMessage(copy.requestedCancel);
    } catch (nextError) {
      setError(getLocalizedError(nextError, language, copy.cancellationError));
    } finally {
      setWorking('');
    }
  };

  const price = Number(settings.proPriceEUR || TELLER_PRO_PRICE_EUR).toFixed(2);
  const order = membership.upgradeOrder;

  return (
    <section id="teller-pro" className={`teller-pro-panel ${state.active ? 'is-pro' : ''}`} aria-labelledby="teller-pro-title">
      <div className="teller-pro-copy">
        <div className="teller-pro-heading">
          <span className="pro-orbit" aria-hidden="true">✦</span>
          <div>
            <p className="eyebrow">{copy.membership}</p>
            <h2 id="teller-pro-title">{copy.title}</h2>
          </div>
          <span className={`pro-plan-badge ${state.active ? 'active' : ''}`}>
            {state.cancellationPending ? copy.cancellationPending : state.active ? copy.active : state.upgradePending ? copy.activationPending : copy.free}
          </span>
        </div>
        <p className="teller-pro-intro">
          {copy.intro}
        </p>
        <ul className="teller-pro-benefits">
          {copy.benefits.map((benefit) => <li key={benefit}><span aria-hidden="true">✓</span>{benefit}</li>)}
        </ul>
      </div>

      <aside className="teller-pro-action" aria-live="polite">
        <span className="pro-price"><strong>€{price}</strong><small>{copy.month}</small></span>
        {loading ? <p className="pro-status-copy">{copy.loading}</p> : null}
        {!loading && state.cancellationPending ? (
          <p className="pro-status-copy">{copy.pendingCancellation}</p>
        ) : null}
        {!loading && state.upgradePending && !state.cancellationPending ? (
          <div className="pro-payment-details">
            <PaymentQr
              amountEUR={Number(order?.amountEUR || price)}
              variableSymbol={order?.variableSymbol}
              note="Fate Room Reader Pro"
              iban={settings.iban}
              beneficiaryName={settings.beneficiaryName}
            />
            <p>{copy.payment}</p>
            <dl>
              <div><dt>{copy.iban}</dt><dd>{settings.iban || copy.support}</dd></div>
              <div><dt>{copy.reference}</dt><dd>{order?.variableSymbol || '—'}</dd></div>
              <div><dt>{copy.amount}</dt><dd>€{Number(order?.amountEUR || price).toFixed(2)}</dd></div>
            </dl>
          </div>
        ) : null}
        {!loading && state.active && !state.cancellationPending ? (
          <p className="pro-status-copy">{copy.activeBody}</p>
        ) : null}

        {!loading && !state.active && !state.upgradePending && !state.cancellationPending ? (
          <button type="button" className="pro-primary-button" onClick={activate} disabled={Boolean(working)}>
            {working === 'activate' ? copy.preparing : copy.get}
          </button>
        ) : null}
        {!loading && (state.active || state.upgradePending) && !state.cancellationPending ? (
          <>
            <p className="pro-status-copy">
              {state.active
                ? copy.pendingCancelActive
                : copy.pendingCancelOrder}
            </p>
            <button type="button" className="pro-cancel-button" onClick={cancel} disabled={Boolean(working)}>
              {working === 'cancel' ? copy.sending : state.active ? copy.cancel : copy.cancelRequest}
            </button>
          </>
        ) : null}
        {message ? <p className="pro-inline-success" role="status">{message}</p> : null}
        {error ? <p className="pro-inline-error" role="alert">{error}</p> : null}
        <small className="pro-fine-print">{copy.finePrint}</small>
      </aside>
    </section>
  );
};

export default TellerProPanel;
